import { useEffect, useState } from 'react';
import './MainSection.scss';
import { useNavigate } from 'react-router-dom';
import Logo from '../../../../components/Atoms/Logo.jsx';
import Button from '../../../../components/Atoms/Button';
import useLogin from '../hooks/useLogin';

/**
 * The sign-in card.
 *
 * TWO FIELDS, AND NO PROVIDER PICKER. The server decides which service the typed
 * credentials belong to; asking the person to declare it first would mean asking
 * them to know something about us rather than about themselves, and getting it
 * wrong would look exactly like a wrong password.
 *
 * The card grows a SECOND STEP when — and only when — the server says it needs
 * one, and both of those steps are a consequence of the same fact:
 *
 *   link       the typed credentials proved a service account, and a LabelFlow
 *              account already sits on that address. Its password is the only
 *              thing that may attach the two, so it is asked for here rather
 *              than reported as a failure.
 *   bootstrap  the workspace was created BY a service sign-in and has no
 *              LabelFlow password yet. It gets one before there is a session.
 *
 * Why the email and password stay on screen, read-only, during those steps: the
 * hook is holding the exact pair it already proved, and an editable field whose
 * value no longer matters is a field somebody corrects and then wonders why
 * nothing changed. Read-only, not hidden, so the address the second password
 * belongs to is still in front of them; read-only, not disabled, so it can still
 * be read and copied.
 */
export default function MainSection(props) {
  const navigate = useNavigate();

  /* THE PAGE PASSES ONLY `handlers`, AND THE PAGE IS NOT THIS CHANGE'S TO EDIT.
     `Login.jsx` renders `<MainSection handlers={handlers} />` and nothing else,
     so `step`, `pending`, `providers` and `notice` arrived undefined — and the
     card then had no second step at all. MSG_LINK_REQUIRED deliberately raises
     no dialog (the FORM is the explanation and the remedy), so the whole answer
     was: the spinner stops, nothing changes, nothing is said. A dead end on the
     one path that exists to stop a stranger's site being attached to somebody
     else's workspace.

     So the card holds its own instance of the hook and uses it WHOLE. Whole is
     the point: `handlers` and `step` must come from ONE instance, or the
     handler that runs belongs to a state nothing is rendering — which is the
     bug above, wearing a fix's clothes. A caller that passes the full set
     (`step` present) wins, so wiring the page up later changes nothing here. */
  const local = useLogin();
  const wired = typeof props.step === 'string';
  const {
    handlers,
    step = 'credentials',
    providers = [],
    pending = false,
    notice = '',
  } = wired ? props : local;

  const [ripple, setRipple] = useState(null);
  // The second-step fields are controlled, unlike the two above them: they are
  // typed once, validated here, and must be gone the moment the step is left.
  const [linkPassword, setLinkPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState('');

  const onCredentials = step === 'credentials';

  // Leaving a step must not leave the password it collected in memory behind a
  // hidden component, and a stale validation message must not greet the next
  // step it has nothing to do with.
  useEffect(() => {
    setLinkPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setFormError('');
  }, [step]);

  /* Which OTHER accounts this form accepts. It is drawn from the catalogue the
     server publishes, so a provider added on the backend announces itself here
     with no frontend change — and it is a sentence, not a control: there is
     nothing to choose. */
  const alsoAccepted = (providers || [])
    .filter((p) => p && p.canLogin && !p.isHome && p.configured)
    .map((p) => p.label)
    .filter(Boolean);

  const submit = async () => {
    if (pending) return;
    setFormError('');

    if (onCredentials) {
      const email = document.querySelector('#txtEmail')?.value?.trim();
      const password = document.querySelector('#txtPassword')?.value;
      if (!email || !password) {
        setFormError('Enter your email address and password.');
        return;
      }
      await handlers.onLogin({ email, password });
      return;
    }

    if (step === 'link') {
      if (!linkPassword) {
        setFormError('Enter the password of the LabelFlow account on this address.');
        return;
      }
      await handlers.onCompleteLink({ labelflowPassword: linkPassword });
      return;
    }

    // bootstrap. Both checks are the server's rules said early: it refuses a
    // password under 8 characters, and a password typed once and never seen
    // again is one nobody can be sure of.
    if (newPassword.length < 8) {
      setFormError('Choose a password of at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setFormError('The two passwords do not match.');
      return;
    }
    await handlers.onBootstrap({ newPassword });
  };

  const handleButtonClick = async (e) => {
    if (pending) return;
    if (e?.currentTarget) {
      const rect = e.currentTarget.getBoundingClientRect();
      setRipple({ x: e.clientX - rect.left, y: e.clientY - rect.top });
      setTimeout(() => setRipple(null), 600);
    }
    await submit();
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      submit();
    }
  };

  const handleSignupRedirect = (e) => {
    e.preventDefault();
    navigate('/Signup');
  };

  const submitLabel = onCredentials
    ? 'Sign In'
    : step === 'link'
      ? 'Link and sign in'
      : 'Set password and continue';

  return (
    <div className="login-box">
      <div className="login-card">
        <div className="login-header">
          <div className="logo-container">
            <Logo size={34} />
          </div>
          <h2 className="login-title">Welcome Back</h2>
          <p className="login-description">Sign in to manage projects, models, and deployments</p>
        </div>

        <form className="login-form" onSubmit={(e) => e.preventDefault()}>
          <div className="form-group form-group--1">
            <label className="form-label" htmlFor="txtEmail">Email Address</label>
            <div className="input-wrapper">
              <svg className="input-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
              <input
                type="email"
                className="form-input"
                id="txtEmail"
                placeholder="you@example.com"
                autoComplete="email"
                readOnly={!onCredentials}
              />
            </div>
          </div>

          <div className="form-group form-group--2">
            <label className="form-label" htmlFor="txtPassword">Password</label>
            <div className="input-wrapper">
              <svg className="input-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <input
                type="password"
                className="form-input"
                id="txtPassword"
                placeholder="Enter your password"
                onKeyDown={handleKeyDown}
                autoComplete="current-password"
                readOnly={!onCredentials}
              />
            </div>
          </div>

          {onCredentials && (
            <div className="form-group form-group--3">
              <div className="form-options">
                <label className="checkbox-label">
                  <input type="checkbox" className="checkbox-input" id="customCheck1" />
                  <span className="checkbox-custom" />
                  <span className="checkbox-text">Remember me</span>
                </label>
              </div>
            </div>
          )}

          {step === 'link' && (
            <div className="login-link-step">
              {/* The server's own sentence, rendered as text. There is no
                  reason-code table in the frontend, so a new one reads
                  correctly the day it ships. */}
              <p className="login-link-step__note" role="status">{notice}</p>
              <div className="form-group form-group--3">
                <label className="form-label" htmlFor="txtLinkPassword">LabelFlow password</label>
                <div className="input-wrapper">
                  <svg className="input-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 17H7A5 5 0 0 1 7 7h2M15 7h2a5 5 0 0 1 0 10h-2M8 12h8" />
                  </svg>
                  <input
                    type="password"
                    className="form-input"
                    id="txtLinkPassword"
                    placeholder="Password of the LabelFlow account"
                    value={linkPassword}
                    onChange={(e) => setLinkPassword(e.target.value)}
                    onKeyDown={handleKeyDown}
                    autoComplete="current-password"
                    autoFocus
                  />
                </div>
              </div>
            </div>
          )}

          {step === 'bootstrap' && (
            <div className="login-link-step">
              <p className="login-link-step__note" role="status">{notice}</p>
              <div className="form-group form-group--3">
                <label className="form-label" htmlFor="txtNewPassword">New LabelFlow password</label>
                <div className="input-wrapper">
                  <svg className="input-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                  <input
                    type="password"
                    className="form-input"
                    id="txtNewPassword"
                    placeholder="At least 8 characters"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                    autoFocus
                  />
                </div>
              </div>
              <div className="form-group form-group--3">
                <label className="form-label" htmlFor="txtConfirmPassword">Confirm password</label>
                <div className="input-wrapper">
                  <svg className="input-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                  <input
                    type="password"
                    className="form-input"
                    id="txtConfirmPassword"
                    placeholder="Type it once more"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onKeyDown={handleKeyDown}
                    autoComplete="new-password"
                  />
                </div>
              </div>
            </div>
          )}

          {formError && (
            <p className="login-link-step__error" role="alert">{formError}</p>
          )}

          <div className="form-group form-group--4">
            <button
              type="button"
              onClick={(e) => handleButtonClick(e)}
              className={`login-button ${pending ? 'login-button--loading' : ''}`}
              disabled={pending}
            >
              {ripple && (
                <span
                  className="login-button__ripple"
                  style={{ left: ripple.x, top: ripple.y }}
                  aria-hidden
                />
              )}
              {pending ? (
                <span className="login-button__spinner" />
              ) : (
                <>
                  <span>{submitLabel}</span>
                  <svg className="button-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </>
              )}
            </button>

            {!onCredentials && (
              <div className="login-link-step__actions">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handlers.onCancelStep}
                  disabled={pending}
                >
                  Use a different account
                </Button>
              </div>
            )}
          </div>
        </form>

        {onCredentials && alsoAccepted.length > 0 && (
          <p className="login-alt">
            Have a {alsoAccepted.join(' or ')} account? Sign in with that email and password —
            the same two fields. We will find the workspace it belongs to.
          </p>
        )}

        <div className="login-footer">
          <p className="footer-text">
            Don&apos;t have an account?{' '}
            <a href="#" onClick={handleSignupRedirect} className="footer-link">
              Sign Up
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
